import classNames from 'classnames'
import { FunctionComponent, KeyboardEvent, useEffect, useState } from 'react'
import { ReactComponent as ArrowLeftSvg } from 'src/assets/svg/arrow-left.svg'
import { ReactComponent as ArrowRightSvg } from 'src/assets/svg/arrow-right.svg'
import { Swiper } from 'swiper/types'
import './SwimlaneNavigation.scss'

interface SwimlaneNavigationProps {
  swiper: Swiper | null
  withCustomPagination: boolean
}

export const SwimlaneNavigation: FunctionComponent<SwimlaneNavigationProps> = ({
  swiper,
  withCustomPagination
}) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [slides, setSlides] = useState<HTMLElement[]>([])

  useEffect(() => {
    if (!swiper) return

    const handleSlideChange = () => {
      setActiveIndex(swiper.activeIndex)
    }

    // Set initial index and slides
    setActiveIndex(swiper.activeIndex)
    setSlides(swiper.slides)

    // Add event listeners
    swiper.on('slideChange', handleSlideChange)
    swiper.on('slideNextTransitionStart', handleSlideChange)
    swiper.on('slidePrevTransitionStart', handleSlideChange)
    swiper.on('init', () => {
      setSlides(swiper.slides)
    })

    return () => {
      swiper.off('slideChange', handleSlideChange)
      swiper.off('slideNextTransitionStart', handleSlideChange)
      swiper.off('slidePrevTransitionStart', handleSlideChange)
      swiper.off('init', () => {
        setSlides(swiper.slides)
      })
    }
  }, [swiper])

  const handlePrev = () => {
    if (!swiper) return
    swiper.slidePrev()
    setActiveIndex(swiper.activeIndex)
  }

  const handleNext = () => {
    if (!swiper) return
    swiper.slideNext()
    setActiveIndex(swiper.activeIndex)
  }

  const handleSlideTo = (index: number) => {
    if (!swiper) return
    swiper.slideTo(index)
    setActiveIndex(index)
  }

  const handleKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleSlideTo(index)
    }
  }

  const handleNavigationKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    action: 'prev' | 'next'
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      if (action === 'prev') {
        handlePrev()
      } else {
        handleNext()
      }
    }
  }

  const generateSlideId = (index: number): string => {
    return `slide-${index}-${swiper?.slides[index]?.getAttribute(
      'data-swiper-slide-index'
    ) ?? index}`
  }

  if (!swiper || slides.length === 0) return null

  if (!withCustomPagination) {
    return (
      <nav aria-label="Slide navigation">
        {!swiper.isBeginning && (
          <button
            className="swimlane-navigation__isolated-button swimlane-navigation__isolated-button--left"
            onClick={handlePrev}
            onKeyDown={e => handleNavigationKeyDown(e, 'prev')}
            aria-label="Previous slide"
            type="button">
            <ArrowLeftSvg aria-hidden="true" />
          </button>
        )}
        {!swiper.isEnd && (
          <button
            className="swimlane-navigation__isolated-button swimlane-navigation__isolated-button--right"
            onClick={handleNext}
            onKeyDown={e => handleNavigationKeyDown(e, 'next')}
            aria-label="Next slide"
            type="button">
            <ArrowRightSvg aria-hidden="true" />
          </button>
        )}
      </nav>
    )
  }

  return (
    <nav className="swimlane-navigation" aria-label="Slide navigation">
      {!swiper.isBeginning ? (
        <button
          className="swimlane-navigation__button"
          onClick={handlePrev}
          onKeyDown={e => handleNavigationKeyDown(e, 'prev')}
          aria-label="Previous slide"
          type="button">
          <ArrowLeftSvg aria-hidden="true" />
        </button>
      ) : (
        <div
          className="swimlane-navigation__button-skeleton"
          aria-hidden="true"
        />
      )}

      <div
        className="swimlane-navigation__pagination"
        role="tablist"
        aria-label="Slide indicators">
        {slides.map((slide, index) => (
          <button
            key={generateSlideId(index)}
            className={classNames('swimlane-navigation__pagination-bullet', {
              'swimlane-navigation__pagination-bullet--active':
                index === activeIndex
            })}
            onClick={() => handleSlideTo(index)}
            onKeyDown={e => handleKeyDown(e, index)}
            role="tab"
            aria-selected={index === activeIndex}
            aria-label={`Go to slide ${index + 1}`}
            type="button"
            tabIndex={0}
          />
        ))}
      </div>

      {!swiper.isEnd ? (
        <button
          className="swimlane-navigation__button"
          onClick={handleNext}
          onKeyDown={e => handleNavigationKeyDown(e, 'next')}
          aria-label="Next slide"
          type="button">
          <ArrowRightSvg aria-hidden="true" />
        </button>
      ) : (
        <div
          className="swimlane-navigation__button-skeleton"
          aria-hidden="true"
        />
      )}
    </nav>
  )
}
